import { FC, memo, useCallback, useContext, useMemo } from 'react'

import { Box, Button, Flex, Menu, Portal, Text } from '@chakra-ui/react'
import { ChevronDownIcon, ResetGridLockIcon } from '@editor/assets/icons'
import { EDITOR_TOOLS, PanelType } from '@editor/config/constants'
import { INFO_PANEL_PADDING } from '@editor/config/styles'
import { EditorContext } from '@editor/contexts/Editor'
import { Timeout } from '@editor/interfaces/attribute'
import {
  GridOrderDirection,
  GridType,
  IntervalsAxisConfig,
  IntervalsConfig,
} from '@editor/interfaces/inspectionItemGrid'
import CollapsePanel from '@editor/pages/projects/editor/infoPanels/components/CollapsePanel'
import { toggleCollapsedPanel } from '@editor/pages/projects/editor/store/editor'
import { RootState, useAppDispatch } from '@editor/store/app'
import { Divider, DividerLine } from '@infraloop/common'
import { isTablet } from 'react-device-detect'
import { useSelector } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'

import { saveGridIntervalPrefs } from '../../gridIntervalPrefs'
import { patchInterval, resetShownWorkingEdgePlanes, setShownWorkingEdgePlanes, updateInterval } from '../../store'
import { calculateInterval } from '../../utils'
import CenterBasedAxisConfig from './CenterBasedAxisConfig'
import EdgeBasedAxisConfig from './EdgeBasedAxisConfig'

let mouseoverTimeout: Timeout | null = null

const GridIntervalPanel: FC = () => {
  // Context
  const { selectedTool } = useContext(EditorContext)

  // Store
  const dispatch = useAppDispatch()
  const intervals = useSelector((state: RootState) => state.toolGrid.intervals)
  const selectedVolumeId = useSelector((state: RootState) => state.toolGrid.selectedVolumeId)
  const shownWorkingEdgePlanes = useSelector((state: RootState) => state.toolGrid.shownWorkingEdgePlanes)
  const collapsedPanel = useSelector((state: RootState) => state.editor.collapsedPanel)

  // Derived state
  const isCollapsed = useMemo(() => collapsedPanel.includes(PanelType.Tool), [collapsedPanel])
  const interval = intervals[selectedVolumeId] || null

  /**
   * Reset interval lock so it would be editable again.
   * Will remove any arbitrary placed grid points.
   */
  const resetIntervalLock = useCallback(() => {
    dispatch(updateInterval({ id: selectedVolumeId, interval: { ...interval, locked: false } }))
  }, [dispatch, interval, selectedVolumeId])

  const patch = useDebouncedCallback((volumeId: string, newData: Partial<IntervalsConfig>) => {
    dispatch(patchInterval({ id: volumeId, interval: newData }))
  }, 200)

  const resetWorkingEdgePlanes = useCallback(
    (id: string, axis: 'shortAxis' | 'longAxis') => {
      if (mouseoverTimeout) {
        clearTimeout(mouseoverTimeout)
      }

      mouseoverTimeout = setTimeout(() => {
        dispatch(resetShownWorkingEdgePlanes({ id, axis }))
      }, 3000)
    },
    [dispatch],
  )

  /**
   * Remember the user's geometry-independent grid spacing preferences so the next
   * brand-new grid can be pre-set with them (see gridIntervalPrefs / useEditor).
   */
  const persistPrefs = useCallback(
    (updated: Partial<IntervalsConfig>) => {
      if (!interval) return
      saveGridIntervalPrefs({
        ...interval,
        ...updated,
        longAxis: { ...interval.longAxis, ...updated.longAxis },
        shortAxis: { ...interval.shortAxis, ...updated.shortAxis },
      })
    },
    [interval],
  )

  const patchLongAxisInterval = (config: Partial<IntervalsAxisConfig>) => {
    const updated = { longAxis: { ...interval.longAxis, ...config } }
    patch(selectedVolumeId, updated)
    persistPrefs(updated)
  }
  const patchShortAxisInterval = (config: Partial<IntervalsAxisConfig>) => {
    const updated = { shortAxis: { ...interval.shortAxis, ...config } }
    patch(selectedVolumeId, updated)
    persistPrefs(updated)
  }

  const handleOnMouseIn = useCallback(
    (axis: 'shortAxis' | 'longAxis') => {
      if (
        mouseoverTimeout &&
        shownWorkingEdgePlanes?.id === selectedVolumeId &&
        shownWorkingEdgePlanes?.axis === axis
      ) {
        clearTimeout(mouseoverTimeout)
      }

      dispatch(setShownWorkingEdgePlanes({ id: selectedVolumeId, axis }))
    },
    [dispatch, shownWorkingEdgePlanes, selectedVolumeId],
  )

  const handleOnMouseOut = useCallback(
    (axis: 'shortAxis' | 'longAxis') => {
      resetWorkingEdgePlanes(selectedVolumeId, axis)
    },
    [resetWorkingEdgePlanes, selectedVolumeId],
  )

  const setGridOrder = (orderDirection: GridOrderDirection) => {
    patch(selectedVolumeId, { orderDirection })
    persistPrefs({ orderDirection })
  }
  const setGridType = (type: GridType) => {
    patch(selectedVolumeId, { type })
    persistPrefs({ type })
  }

  // Switching to edge-based recalculates each axis' interval from its edge distance & point count.
  const setEdgeBased = () => {
    const updated: Partial<IntervalsConfig> = {
      type: GridType.EdgeBased,
      longAxis: {
        ...interval.longAxis,
        interval: {
          ...interval.longAxis.interval,
          value: calculateInterval(
            interval.longAxis.total,
            interval.longAxis.edgeDistance.value,
            interval.longAxis.pointCount.value,
          ),
        },
      },
      shortAxis: {
        ...interval.shortAxis,
        interval: {
          ...interval.shortAxis.interval,
          value: calculateInterval(
            interval.shortAxis.total,
            interval.shortAxis.edgeDistance.value,
            interval.shortAxis.pointCount.value,
          ),
        },
      },
    }
    patch(selectedVolumeId, updated)
    persistPrefs(updated)
  }

  if (selectedTool !== EDITOR_TOOLS.GRID) {
    return null
  }

  return (
    <CollapsePanel
      title="グリッド間隔"
      onChange={() => dispatch(toggleCollapsedPanel(PanelType.Tool))}
      isCollapsed={isCollapsed}
      data-testid="grid-interval-panel"
    >
      {!interval && (
        <Box textAlign="center" py={5}>
          <Text>グリッドを作成する領域の平面を選んでください</Text>
        </Box>
      )}

      {interval?.locked && (
        <Box textAlign="center" py={5}>
          <Text>手動で点を移動したグリッドの間隔は変更できません</Text>
          <Button
            colorPalette="secondary"
            fontSize="xs"
            variant="toolbar"
            size={isTablet ? 'lg' : 'sm'}
            mt={5}
            onClick={resetIntervalLock}
          >
            リセット
            <ResetGridLockIcon size={18} />
          </Button>
        </Box>
      )}

      {interval && !interval.locked && (
        <>
          <Box px={INFO_PANEL_PADDING} pt={INFO_PANEL_PADDING}>
            <Text>番号付けの方向</Text>
            <Menu.Root variant="editorPanel" positioning={{ placement: 'bottom-start', gutter: 4, sameWidth: true }}>
              <Menu.Trigger asChild>
                <Button variant="panel-dropdown">
                  {interval.orderDirection === GridOrderDirection.Vertical ? '縦方向' : '横方向'}
                  <ChevronDownIcon />
                </Button>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    <Menu.Item value="horizontal" onClick={() => setGridOrder(GridOrderDirection.Horizontal)}>
                      横方向
                    </Menu.Item>
                    <Menu.Item value="vertical" onClick={() => setGridOrder(GridOrderDirection.Vertical)}>
                      縦方向
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </Box>

          <Box px={INFO_PANEL_PADDING} py={INFO_PANEL_PADDING}>
            <Text>点の配置方法</Text>
            <Menu.Root variant="editorPanel" positioning={{ placement: 'bottom-start', gutter: 4, sameWidth: true }}>
              <Menu.Trigger asChild>
                <Button variant="panel-dropdown">
                  {interval.type === GridType.CenterBased ? '中心から等間隔に配置' : '端から指定数で配置'}
                  <ChevronDownIcon />
                </Button>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    <Menu.Item value="center-based" onClick={() => setGridType(GridType.CenterBased)}>
                      中心から等間隔に配置
                    </Menu.Item>
                    <Menu.Item value="edge-based" onClick={setEdgeBased}>
                      端から指定数で配置
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </Box>

          <Divider>
            <DividerLine borderColor="gray2.600" />
          </Divider>

          {interval?.type === GridType.CenterBased && (
            <>
              {/* Longer axis */}
              <CenterBasedAxisConfig
                side="長辺"
                selectedVolumeId={selectedVolumeId}
                axisConfig={interval.longAxis}
                patchAxisConfig={patchLongAxisInterval}
              />

              <Flex justifyContent="center" px={INFO_PANEL_PADDING}>
                <Divider>
                  <DividerLine borderColor="gray2.700" />
                </Divider>
              </Flex>

              {/* Shorter axis */}
              <CenterBasedAxisConfig
                side="短辺"
                selectedVolumeId={selectedVolumeId}
                axisConfig={interval.shortAxis}
                patchAxisConfig={patchShortAxisInterval}
              />
            </>
          )}

          {/* ## Edge-based grid config */}
          {interval.type === GridType.EdgeBased && (
            <>
              {/* Edge-based - Longer axis edge distance */}
              <EdgeBasedAxisConfig
                side="長辺"
                selectedVolumeId={selectedVolumeId}
                axisConfig={interval.longAxis}
                patchAxisConfig={patchLongAxisInterval}
                onMouseIn={() => handleOnMouseIn('longAxis')}
                onMouseOut={() => handleOnMouseOut('longAxis')}
              />

              <Flex justifyContent="center" px={INFO_PANEL_PADDING}>
                <Divider>
                  <DividerLine borderColor="gray2.700" />
                </Divider>
              </Flex>

              {/* Edge-based - Shorter axis edge distance */}
              <EdgeBasedAxisConfig
                side="短辺"
                selectedVolumeId={selectedVolumeId}
                axisConfig={interval.shortAxis}
                patchAxisConfig={patchShortAxisInterval}
                onMouseIn={() => handleOnMouseIn('shortAxis')}
                onMouseOut={() => handleOnMouseOut('shortAxis')}
              />
            </>
          )}
        </>
      )}
    </CollapsePanel>
  )
}

export default memo(GridIntervalPanel)
